import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  TablePagination,
  Grid,
  Typography,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Business,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  SwapHoriz as SwapHorizIcon,
  Settings as SettingsIcon,
  Warehouse as WarehouseIcon,
  Report as ReportIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import apiService, { Supplier } from '../../services/apiService';

type CurrentView = 'suppliers';

const Suppliers: React.FC = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<CurrentView>('suppliers');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierTypes, setSupplierTypes] = useState<any[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<any>({});

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierTypeFilter, setSupplierTypeFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiService.getSuppliers();
      setSuppliers(response.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = async (item?: any) => {
    const itemToEdit = item || selectedItem;
    if (itemToEdit) {
      setEditingItemId(itemToEdit.id);
      
      // Prepare form data with proper field names
      let formData = { ...itemToEdit };
      
      if (currentView === 'suppliers') {
        formData = {
          ...itemToEdit,
          supplier_type_id: itemToEdit.supplier_type?.id?.toString() || '',
          payment_type_id: itemToEdit.payment_type?.id?.toString() || '',
        };
      }
      
      setFormData(formData);
      setIsEditing(true);
      setDrawerOpen(true);
      setAnchorEl(null);
      
      // Fetch dropdown data only for suppliers view
      if (currentView === 'suppliers') {
        try {
          const [supplierTypesResponse, paymentTypesResponse] = await Promise.all([
            apiService.getSupplierTypes(),
            apiService.getPaymentTypes(),
          ]);
          setSupplierTypes(supplierTypesResponse.data || []);
          setPaymentTypes(paymentTypesResponse.data || []);
        } catch (error: any) {
          console.error('Failed to fetch dropdown data:', error);
          toast.error('Failed to load dropdown data');
        }
      }
    }
  };

  const handleDeleteClick = (item?: any) => {
    setAnchorEl(null);
    const itemToDelete = item || selectedItem;
    if (itemToDelete) {
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await apiService.deleteSupplier(itemToDelete.id);
            toast.success('Supplier deleted successfully');
            fetchData();
          } catch (error: any) {
            toast.error(error.message || 'Failed to delete supplier');
          }
        }
      });
    }
  };

  const handleAddNew = async () => {
    setFormData({});
    setIsEditing(false);
    setDrawerOpen(true);
    
    // Fetch dropdown data only for suppliers view
    if (currentView === 'suppliers') {
      try {
        const [supplierTypesResponse, paymentTypesResponse] = await Promise.all([
          apiService.getSupplierTypes(),
          apiService.getPaymentTypes(),
        ]);
        setSupplierTypes(supplierTypesResponse.data || []);
        setPaymentTypes(paymentTypesResponse.data || []);
      } catch (error: any) {
        console.error('Failed to fetch dropdown data:', error);
        toast.error('Failed to load dropdown data');
      }
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        name: formData.supplier_name, // Send supplier_name as name for backend validation
        supplier_type_id: formData.supplier_type_id ? parseInt(formData.supplier_type_id) : null,
        payment_type_id: formData.payment_type_id ? parseInt(formData.payment_type_id) : null,
      };
      await apiService.createSupplier(submitData);
      toast.success('Supplier created successfully');
      setDrawerOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create supplier');
    }
  };

  const handleUpdateSubmit = async () => {
    if (!editingItemId) return;
    
    try {
      const submitData = {
        ...formData,
        name: formData.supplier_name, // Send supplier_name as name for backend validation
        supplier_type_id: formData.supplier_type_id ? parseInt(formData.supplier_type_id) : null,
        payment_type_id: formData.payment_type_id ? parseInt(formData.payment_type_id) : null,
      };
      await apiService.updateSupplier(editingItemId, submitData);
      toast.success('Supplier updated successfully');
      setDrawerOpen(false);
      setEditingItemId(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update supplier');
    }
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setEditingItemId(null);
    setFormData({});
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const filteredData = () => {
    let data = suppliers;
    
    if (searchTerm) {
      data = data.filter((item: any) => 
        item.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      data = data.filter((item: any) => item.status === statusFilter);
    }
    if (supplierTypeFilter !== 'all') {
      data = data.filter((item: any) => item.supplier_type?.id === parseInt(supplierTypeFilter));
    }
    if (paymentTypeFilter !== 'all') {
      data = data.filter((item: any) => item.payment_type?.id === parseInt(paymentTypeFilter));
    }

    return data;
  };

  const renderTable = () => {
    const data = filteredData();
    const paginatedData = Array.isArray(data) ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : [];

    return (
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f9fafb' }}>
            <TableCell padding="checkbox">
              <Checkbox />
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Supplier Name</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Contact Person</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Email Address</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Phone Number</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Current Stock</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#374151' }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                Loading suppliers...
              </TableCell>
            </TableRow>
          ) : paginatedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                No suppliers found
              </TableCell>
            </TableRow>
          ) : (
            paginatedData.map((supplier: Supplier) => (
              <TableRow 
                key={supplier.id} 
                hover 
                sx={{ 
                  '&:hover': { backgroundColor: '#f9fafb', cursor: 'pointer' },
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/suppliers/${supplier.id}`)}
              >
                <TableCell padding="checkbox">
                  <Checkbox />
                </TableCell>
                <TableCell sx={{ color: '#374151' }}>{supplier.supplier_name}</TableCell>
                <TableCell sx={{ color: '#374151' }}>{supplier.contact_person || 'N/A'}</TableCell>
                <TableCell sx={{ color: '#374151' }}>{supplier.email || 'N/A'}</TableCell>
                <TableCell sx={{ color: '#374151' }}>{supplier.phone || 'N/A'}</TableCell>
                <TableCell sx={{ color: '#374151', fontWeight: 600 }}>
                  {supplier.current_stock || 0}
                </TableCell>
                <TableCell>
                  <Chip
                    label={supplier.status}
                    size="small"
                    sx={{
                      backgroundColor: supplier.status === 'active' ? '#e0f2f1' : '#fee2e2',
                      color: supplier.status === 'active' ? '#0d9488' : '#dc2626',
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/suppliers/${supplier.id}`);
                      }}
                      sx={{
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#2563eb',
                        },
                      }}
                    >
                      View
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(supplier);
                      }}
                      sx={{
                        backgroundColor: '#e0f2f1',
                        color: '#0d9488',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#ccedea',
                        },
                      }}
                    >
                      Update
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(supplier);
                      }}
                      sx={{
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#fecaca',
                        },
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  };

  const renderFormFields = () => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Supplier Name"
              value={formData.supplier_name || ''}
              onChange={(e) => handleFormChange('supplier_name', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Contact Person"
              value={formData.contact_person || ''}
              onChange={(e) => handleFormChange('contact_person', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Email Address"
              value={formData.email || ''}
              onChange={(e) => handleFormChange('email', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone || ''}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || 'active'}
                onChange={(e) => handleFormChange('status', e.target.value)}
              >
                <SelectMenuItem value="active">Active</SelectMenuItem>
                <SelectMenuItem value="inactive">Inactive</SelectMenuItem>
                <SelectMenuItem value="suspended">Suspended</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Address"
              value={formData.address || ''}
              onChange={(e) => handleFormChange('address', e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Supplier Type</InputLabel>
              <Select
                value={formData.supplier_type_id || ''}
                onChange={(e) => handleFormChange('supplier_type_id', e.target.value)}
              >
                <SelectMenuItem value="">None</SelectMenuItem>
                {supplierTypes.map((type) => (
                  <SelectMenuItem key={type.id} value={type.id}>
                    {type.display_name}
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={formData.payment_type_id || ''}
                onChange={(e) => handleFormChange('payment_type_id', e.target.value)}
              >
                <SelectMenuItem value="">None</SelectMenuItem>
                {paymentTypes.map((type) => (
                  <SelectMenuItem key={type.id} value={type.id}>
                    {type.display_name}
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes || ''}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<Business />}
          onClick={handleAddNew}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Supplier
        </Button>
      </Box>

      {/* Content Area - Full Width */}
      <Box>
          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Total Suppliers
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {suppliers.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Active Suppliers
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {suppliers.filter(s => s.status === 'active').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    New Suppliers (This Month)
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {(() => {
                      return suppliers.filter(item => {
                        const itemDate = new Date(item.created_at);
                        const currentDate = new Date();
                        return itemDate.getMonth() === currentDate.getMonth() && 
                               itemDate.getFullYear() === currentDate.getFullYear();
                      }).length;
                    })()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Search and Action Bar */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    placeholder="Search here"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#4A90E2' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      width: 400,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#e0f2fe',
                        borderColor: '#e0f2fe',
                        '& fieldset': {
                          borderColor: '#e0f2fe',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bae6fd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4A90E2',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: '#374151',
                        '&::placeholder': {
                          color: '#9ca3af',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    sx={{ 
                      px: 3,
                      py: 1.5,
                      borderRadius: '12px',
                      borderColor: '#e0f2fe',
                      backgroundColor: 'white',
                      color: '#4A90E2',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#bae6fd',
                        backgroundColor: '#f0f9ff',
                      },
                    }}
                  >
                    Filter
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  sx={{ 
                    px: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    backgroundColor: '#4A90E2',
                    color: 'white',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#3b82f6',
                    },
                  }}
                >
                  Export
                </Button>
              </Box>

              {/* Data Table */}
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', height: 'calc(100vh - 450px)' }}>
                {renderTable()}
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={(filteredData() as any[])?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Showing"
                labelDisplayedRows={({ from, to, count }) =>
                  `Showing ${from} to ${to} out of ${count} records`
                }
                sx={{
                  '& .MuiTablePagination-selectLabel': {
                    color: '#6b7280',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    color: '#6b7280',
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
      </Box>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 600,
            padding: 3,
          },
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          {isEditing ? 'Edit' : 'Add'} Supplier
        </Typography>
        
        {renderFormFields()}
        
        <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
          <Button variant="outlined" onClick={handleClose} fullWidth>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={isEditing ? handleUpdateSubmit : handleCreateSubmit}
            fullWidth
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Drawer>

    </Box>
  );
};

export default Suppliers;